import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { inventory, ApiError } from "../lib/api";
import { useActiveBranch, useSession } from "../lib/session";
import { QueryError } from "../components/QueryError";

// Inventario: existencias (stock_list) + conteo físico (open/apply) +
// transferencias (create/ship/receive) + ajuste manual. Todo vía RPC reales.
export const Route = createFileRoute("/inventario")({ component: Inventario });

type Tab = "stock" | "count" | "transfer";

function Inventario() {
  const [tab, setTab] = useState<Tab>("stock");
  return (
    <div>
      <h1 className="mb-4 text-2xl font-bold">Inventario</h1>
      <div className="mb-4 flex gap-1 border-b">
        {([["stock", "Existencias"], ["count", "Conteo físico"], ["transfer", "Transferencias"]] as [Tab, string][]).map(([t, label]) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium ${tab === t ? "border-b-2 border-brand text-brand" : "text-grafito-400"}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === "stock" && <Existencias />}
      {tab === "count" && <Conteo />}
      {tab === "transfer" && <Transferencias />}
    </div>
  );
}

// ---------- Existencias + ajuste manual ----------
function Existencias() {
  const branch = useActiveBranch();
  const qc = useQueryClient();
  const [lowOnly, setLowOnly] = useState(false);
  const [adjust, setAdjust] = useState<{ id: string; name: string } | null>(null);
  const PAGE = 100;
  const { data: pages, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["stock", branch, lowOnly],
    queryFn: ({ pageParam }) => inventory.stockList(branch, lowOnly, PAGE, pageParam),
    initialPageParam: 0,
    getNextPageParam: (last, all) => (last.length === PAGE ? all.length * PAGE : undefined),
  });
  const data = pages?.pages.flat() ?? [];

  return (
    <div>
      <QueryError error={error} />
      <label className="mb-3 flex items-center gap-2 text-sm">
        <input type="checkbox" checked={lowOnly} onChange={(e) => setLowOnly(e.target.checked)} /> Solo bajo stock
      </label>
      <div className="overflow-hidden rounded-xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-grafito-50 text-left text-grafito-400">
            <tr><th className="px-4 py-2">SKU</th><th className="px-4 py-2">Producto</th><th className="px-4 py-2 text-right">Existencia</th><th className="px-4 py-2 text-right">Disponible</th><th className="px-4 py-2" /></tr>
          </thead>
          <tbody>
            {isLoading ? <tr><td colSpan={5} className="px-4 py-8 text-center text-grafito-400">Cargando…</td></tr>
              : data.map((s) => (
                <tr key={s.product_id} className="border-t">
                  <td className="px-4 py-2 font-mono text-xs">{s.sku}</td>
                  <td className="px-4 py-2">{s.name}</td>
                  <td className="px-4 py-2 text-right">{s.qty_base}</td>
                  <td className="px-4 py-2 text-right">{s.qty_available}</td>
                  <td className="px-4 py-2 text-right">
                    {s.is_low && <span className="mr-2 rounded bg-brand-50 px-2 py-0.5 text-xs text-brand-700">Bajo</span>}
                    <button onClick={() => setAdjust({ id: s.product_id, name: s.name })} className="text-xs text-grafito-400 hover:text-brand">ajustar</button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {hasNextPage && (
        <div className="mt-3 text-center">
          <button onClick={() => fetchNextPage()} disabled={isFetchingNextPage} className="btn btn-ghost text-sm disabled:opacity-40">
            {isFetchingNextPage ? "Cargando…" : "Cargar más"}
          </button>
        </div>
      )}
      {adjust && <AdjustModal branch={branch} product={adjust} onDone={() => { setAdjust(null); void qc.invalidateQueries({ queryKey: ["stock"] }); }} onCancel={() => setAdjust(null)} />}
    </div>
  );
}

function AdjustModal({ branch, product, onDone, onCancel }: { branch: string; product: { id: string; name: string }; onDone: () => void; onCancel: () => void }) {
  const [qty, setQty] = useState(""); const [reason, setReason] = useState(""); const [msg, setMsg] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => inventory.adjust(branch, product.id, parseFloat(qty), reason),
    onSuccess: onDone, onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });
  return (
    <Modal title={`Ajustar stock — ${product.name}`} onCancel={onCancel}>
      {msg && <Err msg={msg} />}
      <p className="mb-2 text-xs text-grafito-400">Cantidad: positiva entra, negativa es merma. En unidad base.</p>
      <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Ej: -5" className="mb-3 w-full rounded-lg border px-3 py-2" />
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo (obligatorio)" className="mb-3 w-full rounded-lg border px-3 py-2" />
      <Actions onCancel={onCancel} onOk={() => m.mutate()} okLabel="Aplicar ajuste" disabled={!qty || !reason.trim() || m.isPending} />
    </Modal>
  );
}

// ---------- Conteo físico ----------
interface CountLine { product_id: string; qty_system: number; counted: string; sku: string; name: string }

function Conteo() {
  const branch = useActiveBranch();
  const qc = useQueryClient();
  const [countId, setCountId] = useState<string | null>(null);
  const [lines, setLines] = useState<CountLine[]>([]);
  const [msg, setMsg] = useState<string | null>(null);

  const openCount = useMutation({
    mutationFn: async () => {
      const c = await inventory.openCount(branch) as { id: string };
      // leer las líneas que el RPC sembró (un row por producto con stock)
      const { data } = await supabase.from("inventory_count_lines")
        .select("product_id, qty_system, product:products(sku, name)").eq("count_id", c.id);
      return { id: c.id, lines: (data ?? []) as unknown as { product_id: string; qty_system: number; product: { sku: string; name: string } }[] };
    },
    onSuccess: ({ id, lines }) => {
      setCountId(id);
      setLines(lines.map((l) => ({ product_id: l.product_id, qty_system: l.qty_system, counted: String(l.qty_system), sku: l.product.sku, name: l.product.name })));
    },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });

  const apply = useMutation({
    mutationFn: () => inventory.applyCount(countId!, lines.map((l) => ({ product_id: l.product_id, qty_counted: parseFloat(l.counted) || 0 }))),
    onSuccess: () => { setCountId(null); setLines([]); setMsg("Conteo aplicado: ajustes generados."); void qc.invalidateQueries({ queryKey: ["stock"] }); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : String(e)),
  });

  if (!countId) {
    return (
      <div className="rounded-xl border bg-white p-8 text-center">
        {msg && <p className="mb-3 text-sm text-grafito-700">{msg}</p>}
        <p className="mb-4 text-grafito-400">Inicia un conteo físico: el sistema toma una foto del stock actual y captura lo que cuentes.</p>
        <button onClick={() => openCount.mutate()} disabled={openCount.isPending} className="btn btn-primary disabled:opacity-40">
          {openCount.isPending ? "Abriendo…" : "Iniciar conteo"}
        </button>
      </div>
    );
  }

  return (
    <div>
      {msg && <Err msg={msg} />}
      <div className="overflow-hidden rounded-xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-grafito-50 text-left text-grafito-400">
            <tr><th className="px-4 py-2">Producto</th><th className="px-4 py-2 text-right">Sistema</th><th className="px-4 py-2 text-right">Contado</th><th className="px-4 py-2 text-right">Diferencia</th></tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const diff = (parseFloat(l.counted) || 0) - l.qty_system;
              return (
                <tr key={l.product_id} className="border-t">
                  <td className="px-4 py-2">{l.name}<div className="font-mono text-xs text-grafito-400">{l.sku}</div></td>
                  <td className="px-4 py-2 text-right text-grafito-400">{l.qty_system}</td>
                  <td className="px-4 py-2 text-right">
                    <input type="number" value={l.counted} onChange={(e) => setLines((cur) => { const c = [...cur]; c[i] = { ...c[i], counted: e.target.value }; return c; })}
                      className="w-24 rounded border px-2 py-1 text-right" />
                  </td>
                  <td className={`px-4 py-2 text-right font-medium ${diff !== 0 ? "text-brand" : "text-grafito-400"}`}>{diff > 0 ? `+${diff}` : diff}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={() => { setCountId(null); setLines([]); }} className="btn btn-ghost">Cancelar</button>
        <button onClick={() => apply.mutate()} disabled={apply.isPending} className="btn btn-primary disabled:opacity-40">
          {apply.isPending ? "Aplicando…" : "✓ Aplicar conteo"}
        </button>
      </div>
    </div>
  );
}

// ---------- Transferencias ----------
function Transferencias() {
  const branch = useActiveBranch();
  const { branches } = useSession();
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const others = branches.filter((b) => b.id !== branch);

  // OCs… digo, transferencias en tránsito hacia/desde esta sucursal.
  const { data: transfers } = useQuery({
    queryKey: ["transfers", branch],
    queryFn: async () => {
      const { data } = await supabase.from("stock_transfers")
        .select("id, from_branch_id, to_branch_id, status, created_at")
        .or(`from_branch_id.eq.${branch},to_branch_id.eq.${branch}`)
        .in("status", ["requested", "in_transit"]).order("created_at", { ascending: false });
      return (data ?? []) as { id: string; from_branch_id: string; to_branch_id: string; status: string }[];
    },
  });

  const ship = useMutation({ mutationFn: (id: string) => inventory.transferShip(id), onSuccess: () => refresh(), onError: err });
  const receive = useMutation({ mutationFn: (id: string) => inventory.transferReceive(id), onSuccess: () => { refresh(); void qc.invalidateQueries({ queryKey: ["stock"] }); }, onError: err });
  function refresh() { void qc.invalidateQueries({ queryKey: ["transfers", branch] }); }
  function err(e: unknown) { setMsg(e instanceof ApiError ? e.message : String(e)); }

  return (
    <div>
      {msg && <Err msg={msg} />}
      <NewTransfer from={branch} others={others} onDone={refresh} />
      <h3 className="mb-2 mt-6 font-semibold">En tránsito</h3>
      <div className="overflow-hidden rounded-xl border bg-white">
        <table className="w-full text-sm">
          <thead className="bg-grafito-50 text-left text-grafito-400">
            <tr><th className="px-4 py-2">Transferencia</th><th className="px-4 py-2">Estado</th><th className="px-4 py-2">Dirección</th><th className="px-4 py-2" /></tr>
          </thead>
          <tbody>
            {(transfers ?? []).length === 0 ? <tr><td colSpan={4} className="px-4 py-6 text-center text-grafito-400">Nada en tránsito.</td></tr>
              : transfers!.map((t) => {
                const outgoing = t.from_branch_id === branch;
                return (
                  <tr key={t.id} className="border-t">
                    <td className="px-4 py-2 font-mono text-xs">{t.id.slice(0, 8)}</td>
                    <td className="px-4 py-2"><span className="rounded bg-grafito-100 px-2 py-0.5 text-xs">{t.status}</span></td>
                    <td className="px-4 py-2 text-xs">{outgoing ? "saliente →" : "← entrante"}</td>
                    <td className="px-4 py-2 text-right">
                      {t.status === "requested" && outgoing && <button onClick={() => ship.mutate(t.id)} className="btn btn-ghost py-1 text-xs">Despachar</button>}
                      {t.status === "in_transit" && !outgoing && <button onClick={() => receive.mutate(t.id)} className="btn btn-primary py-1 text-xs">Recibir</button>}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NewTransfer({ from, others, onDone }: { from: string; others: { id: string; name: string }[]; onDone: () => void }) {
  const [to, setTo] = useState(""); const [sku, setSku] = useState(""); const [qty, setQty] = useState(""); const [msg, setMsg] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: async () => {
      // resolver producto por SKU (RLS)
      const { data: prod } = await supabase.from("products").select("id").eq("sku", sku.trim()).maybeSingle();
      if (!prod) throw new Error(`SKU ${sku} no encontrado`);
      return inventory.transferCreate(from, to, [{ product_id: (prod as { id: string }).id, qty_base: parseFloat(qty) }]);
    },
    onSuccess: () => { setSku(""); setQty(""); onDone(); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : (e as Error).message),
  });

  if (others.length === 0) return <p className="text-sm text-grafito-400">Necesitas otra sucursal para transferir (plan Business+).</p>;

  return (
    <div className="rounded-xl border bg-white p-4">
      <h3 className="mb-3 font-semibold">Nueva transferencia</h3>
      {msg && <Err msg={msg} />}
      <div className="flex flex-wrap items-end gap-3">
        <div><label className="mb-1 block text-xs text-grafito-400">Destino</label>
          <select value={to} onChange={(e) => setTo(e.target.value)} className="rounded-lg border px-3 py-2 text-sm">
            <option value="">— elegir —</option>
            {others.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="SKU" className="rounded-lg border px-3 py-2 text-sm" />
        <input type="number" value={qty} onChange={(e) => setQty(e.target.value)} placeholder="Cantidad (base)" className="w-36 rounded-lg border px-3 py-2 text-sm" />
        <button onClick={() => create.mutate()} disabled={!to || !sku || !qty || create.isPending} className="btn btn-primary disabled:opacity-40">Crear</button>
      </div>
    </div>
  );
}

// ---------- helpers UI ----------
function Modal({ title, children, onCancel }: { title: string; children: React.ReactNode; onCancel: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="mb-4 text-lg font-bold">{title}</h2>
        {children}
      </div>
    </div>
  );
}
function Actions({ onCancel, onOk, okLabel, disabled }: { onCancel: () => void; onOk: () => void; okLabel: string; disabled: boolean }) {
  return <div className="flex gap-2"><button onClick={onCancel} className="btn btn-ghost flex-1">Cancelar</button><button onClick={onOk} disabled={disabled} className="btn btn-primary flex-1 disabled:opacity-40">{okLabel}</button></div>;
}
function Err({ msg }: { msg: string }) { return <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{msg}</div>; }
